















import { defineComponent, ref, watch, onMounted, nextTick } from '@vue/composition-api';
import 'highlight.js/styles/github.css';

let libsLoaded = false;
let markedInstance: any = null;
let DOMPurifyInstance: any = null;

async function loadLibs() {
  if (libsLoaded) {
    return;
  }
  const [{ marked }, DOMPurifyMod, hljsMod] = await Promise.all([
    import('marked'),
    import('dompurify'),
    import('highlight.js/lib/core')
  ]);
  const DOMPurify = (DOMPurifyMod as any).default || DOMPurifyMod;
  const hljs = (hljsMod as any).default || hljsMod;

  const langModules = await Promise.all([
    import('highlight.js/lib/languages/javascript'),
    import('highlight.js/lib/languages/typescript'),
    import('highlight.js/lib/languages/python'),
    import('highlight.js/lib/languages/java'),
    import('highlight.js/lib/languages/json'),
    import('highlight.js/lib/languages/bash'),
    import('highlight.js/lib/languages/sql'),
    import('highlight.js/lib/languages/xml'),
    import('highlight.js/lib/languages/css'),
    import('highlight.js/lib/languages/yaml'),
    import('highlight.js/lib/languages/go'),
    import('highlight.js/lib/languages/shell')
  ]);

  const langNames = [
    'javascript', 'typescript', 'python', 'java', 'json',
    'bash', 'sql', 'xml', 'css', 'yaml', 'go', 'shell'
  ];

  langNames.forEach((name, i) => {
    const mod = (langModules[i] as any).default || langModules[i];
    hljs.registerLanguage(name, mod);
  });

  const langAliases: Record<string, string> = {
    js: 'javascript',
    ts: 'typescript',
    py: 'python',
    sh: 'bash',
    html: 'xml',
    htm: 'xml',
    vue: 'xml',
    yml: 'yaml',
    golang: 'go',
    rs: 'go',
    zsh: 'bash'
  };

  marked.setOptions({ gfm: true, breaks: false });

  const renderer = {
    code(token: { text: string; lang?: string }) {
      const code = token.text;
      let language = (token.lang || '').toLowerCase().trim();
      if (langAliases[language]) {
        language = langAliases[language];
      }
      let highlighted: string;
      try {
        if (language && hljs.getLanguage(language)) {
          highlighted = hljs.highlight(code, { language }).value;
        } else {
          highlighted = hljs.highlightAuto(code).value;
        }
      } catch {
        highlighted = escapeHtml(code);
      }
      return `<pre><code class="hljs language-${language || 'plaintext'}">${highlighted}</code></pre>`;
    }
  };
  marked.use({ renderer });

  markedInstance = marked;
  DOMPurifyInstance = DOMPurify;
  libsLoaded = true;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'del', 'code', 'pre', 'blockquote',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'input',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'a', 'img', 'hr', 'span', 'div',
    'sup', 'sub', 'dl', 'dt', 'dd', 'details', 'summary'
  ],
  ALLOWED_ATTR: [
    'href', 'title', 'alt', 'src', 'class',
    'type', 'checked', 'disabled',
    'target', 'rel', 'loading',
    'colspan', 'rowspan', 'align', 'width', 'height'
  ],
  ALLOW_DATA_ATTR: false
};

export default defineComponent({
  name: 'MarkdownPreview',
  props: {
    src: { type: String, required: true },
    name: { type: String, default: '' }
  },
  setup(props) {
    const status = ref<'loading' | 'error' | 'success'>('loading');
    const errorMsg = ref('');
    const safeHtml = ref('');
    const contentRef = ref<HTMLElement>();

    async function render() {
      status.value = 'loading';
      errorMsg.value = '';
      safeHtml.value = '';

      try {
        await loadLibs();

        const response = await fetch(props.src);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const text = await response.text();

        let rawHtml: string;
        try {
          rawHtml = markedInstance.parse(text);
        } catch {
          rawHtml = `<pre>${escapeHtml(text)}</pre>`;
        }

        const sanitized = DOMPurifyInstance.sanitize(rawHtml, SANITIZE_CONFIG);
        safeHtml.value = sanitized;
        status.value = 'success';

        await nextTick();
        postProcessDom();
      } catch (err: any) {
        status.value = 'error';
        errorMsg.value = err?.message || 'Failed to load';
      }
    }

    function postProcessDom() {
      if (!contentRef.value) {
        return;
      }
      const links = contentRef.value.querySelectorAll('a[href]');
      links.forEach((a: Element) => {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      });
      const imgs = contentRef.value.querySelectorAll('img');
      imgs.forEach((img: Element) => {
        if (!img.getAttribute('loading')) {
          img.setAttribute('loading', 'lazy');
        }
      });
      const checkboxes = contentRef.value.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach((cb: Element) => {
        (cb as HTMLInputElement).disabled = true;
      });
    }

    onMounted(() => {
      render();
    });

    watch(() => props.src, () => {
      render();
    });

    return {
      status,
      errorMsg,
      safeHtml,
      contentRef
    };
  }
});
